import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Phone, GraduationCap, CalendarClock } from "lucide-react";

const DONE = new Set(["completed", "done", "cancelled"]);

function TaskRow({ t, onToggle }) {
  const done = DONE.has(t.status);
  return (
    <li className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0" data-testid={`protege-task-${t.id}`}>
      <Checkbox checked={done} onCheckedChange={() => onToggle(t)} className="mt-0.5" data-testid={`protege-task-toggle-${t.id}`} />
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium ${done ? "text-slate-400 line-through" : "text-slate-900"}`}>{t.title}</p>
        <p className="text-xs text-slate-500">{t.template_phase || "Program"}{t.due_date ? ` · Due ${t.due_date.slice(0, 10)}` : ""}</p>
      </div>
    </li>
  );
}

function ProtegeCard({ item, onToggle }) {
  const open = item.tasks.filter((t) => !DONE.has(t.status)).length;
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`protege-card-${item.enrollment_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900" data-testid="protege-name">{item.person.name}</h2>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
            {item.person.email && <a href={`mailto:${item.person.email}`} className="inline-flex items-center gap-1 hover:text-[#0D9488]" data-testid="protege-email"><Mail className="w-3.5 h-3.5" />{item.person.email}</a>}
            {item.person.phone && <a href={`tel:${item.person.phone}`} className="inline-flex items-center gap-1 hover:text-[#0D9488]" data-testid="protege-phone"><Phone className="w-3.5 h-3.5" />{item.person.phone}</a>}
          </div>
        </div>
        <div className="text-right text-sm">
          <span className="inline-flex items-center gap-1 rounded-full bg-[#0F2B5B]/5 px-2.5 py-1 font-medium text-[#0F2B5B]"><GraduationCap className="w-3.5 h-3.5" />{item.program_label}</span>
          <p className="mt-1 text-slate-500" data-testid="protege-stage">Stage: {item.stage || "Getting started"}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Your sponsoring tasks · {open} open</h3>
        {item.upcoming_count > 0 && <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-testid="protege-upcoming"><CalendarClock className="w-3.5 h-3.5" />{item.upcoming_count} upcoming</span>}
      </div>
      {item.tasks.length ? <ul className="mt-1">{item.tasks.map((t) => <TaskRow key={t.id} t={t} onToggle={onToggle} />)}</ul>
        : <p className="mt-2 text-sm text-slate-500">No active tasks yet. They appear here as your Protégé reaches each stage.</p>}
    </section>
  );
}

export default function MyProteges() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    api.get("/sponsorship/my-proteges").then((r) => setItems(r.data.items || [])).catch((e) => { toast.error(apiError(e)); setItems([]); });
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (t) => {
    try {
      await api.post(`/sponsorship/tasks/${t.id}/status`, { status: DONE.has(t.status) ? "not_started" : "completed" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mx-auto max-w-4xl space-y-5 p-4 sm:p-8" data-testid="my-proteges-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">My Protégés</h1>
        <p className="mt-1 text-sm text-slate-500">The Protégés you sponsor and the coaching tasks assigned to you.</p>
      </div>
      {items === null ? <p className="text-sm text-slate-500">Loading…</p>
        : items.length === 0 ? <p className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="my-proteges-empty">You aren't linked as Sponsoring Agent for any active Protégé yet.</p>
        : items.map((it) => <ProtegeCard key={it.enrollment_id} item={it} onToggle={toggle} />)}
    </div>
  );
}
